import React, { useState, useCallback, useMemo } from 'react';
import { useController, Control } from 'react-hook-form';
import { FormField, SpaceBetween, Box, Alert, Modal, Input, Grid } from '@cloudscape-design/components';
import { Button } from '../../Button';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  closestCenter,
  UniqueIdentifier,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormField as FormFieldType } from '../../../../types/formSchema';
import { ColumnMetadata } from '../../../../utils/mockDataManager';
import './column-mapper-field.scss';
import { logger } from '../../../../utils/logger';

// Zone configuration interface
export interface ColumnMapperZone {
  id: string;
  label: string;
  required?: boolean;
  multiple?: boolean;
  maxItems?: number;
  accepts?: string[]; // Column types that can be dropped here
  placeholder?: string;
  validation?: {
    required?: boolean;
    message?: string;
  };
}

// Custom rate metric configuration
export interface RateMetricConfig {
  type: 'custom';
  name: string;
  description?: string;
  numerator: string;
  denominator: string;
  id: string; // Unique identifier for drag-and-drop
}

// Column mapper field configuration
export interface ColumnMapperField {
  type: 'columnMapper';
  name: string;
  zones: ColumnMapperZone[];
  availableColumnsFrom?: string;
  customMetricCreation?: boolean;
  validation?: any;
}

// Drag item types
type DragItem = {
  id: string;
  type: 'column' | 'custom-rate';
  data: ColumnMetadata | RateMetricConfig | string; // Allow string for column names
  sourceZone?: string;
  index?: number;
};

// Helper function to get placeholder examples based on zone type
const getPlaceholderExample = (zoneId: string): string => {
  const examples: Record<string, string> = {
    'baseMetrics': 'e.g., Cost, Revenue, COGS',
    'volumeMetrics': 'e.g., Units, Hours, Volume', 
    'rateMetrics': 'e.g., CPU, Wage_Rate, or custom calculations',
    'mixDimensions': 'e.g., Region, FC_Type, Size',
    // Scenario and Period don't show examples in Figma
  };
  return examples[zoneId] || '';
};

// Helper function to get main placeholder text based on zone type
const getMainPlaceholderText = (zoneId: string, zoneLabel: string): string => {
  const mainTexts: Record<string, string> = {
    'baseMetrics': 'Drop base metric columns here',
    'volumeMetrics': 'Drop volume metric columns here',
    'rateMetrics': 'Drop rate metric columns here or create custom metrics',
    'mixDimensions': 'Drop mix dimension columns here',
    'scenario': 'Drop scenario column here',
    'period': 'Drop period column here'
  };
  return mainTexts[zoneId] || `Drop ${zoneLabel.toLowerCase()} columns here`;
};

// Draggable Available Column Component
const DraggableColumn: React.FC<{
  column: ColumnMetadata;
  isAssigned: boolean;
}> = ({ column, isAssigned }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `available-${column.name}`,
    data: {
      type: 'column',
      data: column,
    },
    disabled: isAssigned,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`column-mapper__available-column ${
        isAssigned ? 'column-mapper__available-column--assigned' : ''
      } ${isDragging ? 'column-mapper__available-column--dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className="lucide lucide-grip-vertical h-4 w-4" 
        aria-hidden="true"
      >
        <circle cx="9" cy="12" r="1"></circle>
        <circle cx="9" cy="5" r="1"></circle>
        <circle cx="9" cy="19" r="1"></circle>
        <circle cx="15" cy="12" r="1"></circle>
        <circle cx="15" cy="5" r="1"></circle>
        <circle cx="15" cy="19" r="1"></circle>
      </svg>
      <span className="column-mapper__available-column-text">{column.name}</span>
    </div>
  );
};

// Sortable Assigned Column Component
const SortableAssignedColumn: React.FC<{
  item: string | RateMetricConfig;
  zoneId: string;
  availableColumns: ColumnMetadata[];
  onRemove: () => void;
  index: number;
}> = ({ item, zoneId, availableColumns, onRemove, index }) => {
  const isCustom = typeof item === 'object';
  const itemId = isCustom ? item.id : `${zoneId}-${item}`;
  const columnName = isCustom ? item.name : item;
  const columnType = isCustom ? 'CUSTOM' : availableColumns.find(col => col.name === item)?.dataType || 'UNKNOWN';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: itemId,
    data: {
      type: isCustom ? 'custom-rate' : 'column',
      data: item,
      sourceZone: zoneId,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Get the display label for the type badge
  const getTypeBadgeLabel = (type: string, isCustom: boolean) => {
    if (isCustom) return 'Custom';
    switch (type) {
      case 'STRING': return 'Text';
      case 'INTEGER': return 'Number';
      case 'DECIMAL': return 'Decimal';
      default: return type;
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`column-mapper__assigned-column ${isDragging ? 'column-mapper__assigned-column--dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <div className="column-mapper__assigned-content">
        <span className="column-mapper__drag-handle">⋮⋮</span>
        
        <span className="column-mapper__assigned-name">{columnName}</span>
        
        <span className="column-mapper__position-badge">
          {index + 1}
        </span>
        
        {index === 0 && (
          <span className="column-mapper__primary-badge">
            Primary
          </span>
        )}
        
        <button
          className="column-mapper__remove-button"
          onClick={onRemove}
          aria-label={`Remove ${columnName}`}
        >
          ×
        </button>
      </div>
      
      {isCustom && (
        <div className="column-mapper__custom-formula">
          {(item as RateMetricConfig).numerator} / {(item as RateMetricConfig).denominator}
        </div>
      )}
    </div>
  );
};

// Droppable Zone Component
const DroppableZone: React.FC<{
  zone: ColumnMapperZone;
  assignedColumns: (string | RateMetricConfig)[];
  availableColumns: ColumnMetadata[];
  onRemove: (index: number) => void;
  validationError?: string;
  isOver?: boolean;
  dragValidation?: { isValid: boolean; reason?: string };
  isDragging?: boolean;
}> = ({ 
  zone, 
  assignedColumns, 
  availableColumns,
  onRemove, 
  validationError,
  isOver = false,
  dragValidation,
  isDragging = false
}) => {
  // Make this zone droppable
  const { setNodeRef, isOver: droppableIsOver } = useDroppable({
    id: zone.id,
    data: {
      type: 'zone',
      zone,
    },
  });

  // Create sortable items with unique IDs
  const sortableItems = assignedColumns.map((item, index) => {
    const isCustom = typeof item === 'object';
    return isCustom ? item.id : `${zone.id}-${item}`;
  });

  return (
    <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
      <div className="column-mapper__zone-container">
        {/* Zone header positioned outside the dashed container */}
        <div className="column-mapper__zone-header">
          <span className="column-mapper__zone-label">
            {zone.label}
          </span>
          <span className="column-mapper__zone-count">
            Drag to reorder
          </span>
        </div>
        
        {/* Dashed drop container */}
        <div 
          ref={setNodeRef}
          className={`column-mapper__drop-zone ${
            assignedColumns.length > 0 ? 'column-mapper__drop-zone--populated' : ''
          } ${
            droppableIsOver ? 'column-mapper__drop-zone--over' : ''
          } ${
            isDragging && dragValidation && !dragValidation.isValid ? 'column-mapper__drop-zone--disabled' : ''
          }`}
        >
          <div className="column-mapper__zone-content">
            {assignedColumns.length === 0 ? (
              <div className="column-mapper__zone-placeholder">
                {isDragging && dragValidation && !dragValidation.isValid ? (
                  <>
                    <span className="placeholder-main placeholder-disabled">
                      Cannot drop here
                    </span>
                    <span className="placeholder-example placeholder-disabled">
                      {dragValidation.reason}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="placeholder-main">
                      {zone.placeholder || getMainPlaceholderText(zone.id, zone.label)}
                    </span>
                    {getPlaceholderExample(zone.id) && (
                      <span className="placeholder-example">
                        {getPlaceholderExample(zone.id)}
                      </span>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="column-mapper__assigned-columns">
                {assignedColumns.map((column, index) => (
                  <SortableAssignedColumn
                    key={typeof column === 'string' ? column : column.id}
                    item={column}
                    zoneId={zone.id}
                    availableColumns={availableColumns}
                    onRemove={() => onRemove(index)}
                    index={index}
                  />
                ))}
              </div>
            )}
          </div>
          
        </div>
      </div>
    </SortableContext>
  );
};

// Custom Rate Metric Modal
const CustomRateMetricModal: React.FC<{
  visible: boolean;
  onDismiss: () => void;
  onConfirm: (config: RateMetricConfig) => void;
  availableColumns: ColumnMetadata[];
}> = ({ visible, onDismiss, onConfirm, availableColumns }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [numerator, setNumerator] = useState('');
  const [denominator, setDenominator] = useState('');

  const numericColumns = useMemo(() => 
    availableColumns.filter(col => ['INTEGER', 'DECIMAL'].includes(col.dataType)),
    [availableColumns]
  );

  const handleConfirm = () => {
    if (name && numerator && denominator) {
      onConfirm({
        type: 'custom',
        name,
        description,
        numerator,
        denominator,
        id: `custom-rate-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      });
      // Reset form
      setName('');
      setDescription('');
      setNumerator('');
      setDenominator('');
    }
  };

  const isValid = name.trim() && numerator && denominator;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Create Custom Rate Metric"
      closeAriaLabel="Close modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="secondary" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} disabled={!isValid}>
              Create Metric
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField label="Metric Name" errorText={!name.trim() ? "Name is required" : undefined}>
          <Input
            value={name}
            onChange={({ detail }) => setName(detail.value)}
            placeholder="e.g., Cost per Unit"
          />
        </FormField>

        <FormField label="Description (Optional)">
          <Input
            value={description}
            onChange={({ detail }) => setDescription(detail.value)}
            placeholder="Brief description of this metric"
          />
        </FormField>

        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          <FormField label="Numerator" errorText={!numerator ? "Numerator is required" : undefined}>
            <select
              value={numerator}
              onChange={(e) => setNumerator(e.target.value)}
              className="column-mapper__select"
            >
              <option value="">Select numerator column</option>
              {numericColumns.map(col => (
                <option key={col.name} value={col.name}>
                  {col.name} ({col.dataType})
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Denominator" errorText={!denominator ? "Denominator is required" : undefined}>
            <select
              value={denominator}
              onChange={(e) => setDenominator(e.target.value)}
              className="column-mapper__select"
            >
              <option value="">Select denominator column</option>
              {numericColumns.map(col => (
                <option key={col.name} value={col.name}>
                  {col.name} ({col.dataType})
                </option>
              ))}
            </select>
          </FormField>
        </Grid>

        {numerator && denominator && (
          <Alert type="info">
            This will create: <strong>{name || 'Custom Metric'}</strong> = {numerator} ÷ {denominator}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
};

// Drag Overlay Component
const DragOverlayContent: React.FC<{ dragItem: DragItem | null }> = ({ dragItem }) => {
  if (!dragItem) return null;

  const isCustom = dragItem.type === 'custom-rate';
  const data = dragItem.data as ColumnMetadata | RateMetricConfig;
  const name = isCustom ? (data as RateMetricConfig).name : (data as ColumnMetadata).name;
  const type = isCustom ? 'CUSTOM' : (data as ColumnMetadata).dataType;

  return (
    <div className="column-mapper__drag-overlay">
      <div className="column-mapper__drag-overlay-header">
        <span className="column-mapper__drag-handle">⋮⋮</span>
        <span className="column-mapper__drag-overlay-name">{name}</span>
      </div>
      <div className="column-mapper__drag-overlay-meta">
        <span className={`column-mapper__column-type column-mapper__column-type--${type.toLowerCase()}`}>
          {type}
        </span>
      </div>
    </div>
  );
};

// Main ColumnMapper field component with drag-and-drop
export interface FieldComponentProps {
  field: FormFieldType;
  control: Control<any>;
  dynamicOptions?: Record<string, any[]>;
  onFieldChange?: (fieldName: string, value: any) => void;
}

export const ColumnMapperFieldComponent: React.FC<FieldComponentProps> = ({
  field,
  control,
  onFieldChange,
}) => {
  const columnMapperField = field as unknown as ColumnMapperField;
  const [customRateModalVisible, setCustomRateModalVisible] = useState(false);
  const [activeDragItem, setActiveDragItem] = useState<DragItem | null>(null);
  const [overId, setOverId] = useState<UniqueIdentifier | null>(null);
  const [dragValidation, setDragValidation] = useState<Record<string, { isValid: boolean; reason?: string }>>({});

  // Safety check: ensure zones exist with default empty array
  const zones = columnMapperField.zones || [];
  
  const {
    field: controllerField,
    fieldState: { error },
  } = useController({
    name: field.name,
    control,
    rules: field.validation ? { required: field.validation.some((v: any) => v.required) } : {},
  });

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get available columns from form context
  const availableColumns: ColumnMetadata[] = useMemo(() => {
    // This would be populated by the upload field API response
    // For now, return mock data for testing
    return [
      {
        name: "FC",
        dataType: "STRING" as const,
        nullable: false,
        availableOptions: ["Sort", "NonSort"],
        statistics: { distinctCount: 2, nullCount: 0 },
        suggested: ["mixDimensions"]
      },
      {
        name: "Size",
        dataType: "STRING" as const,
        nullable: false,
        availableOptions: ["Large", "Small"],
        statistics: { distinctCount: 2, nullCount: 0 },
        suggested: ["mixDimensions"]
      },
      {
        name: "Cost",
        dataType: "INTEGER" as const,
        nullable: false,
        statistics: { distinctCount: 6, nullCount: 0, min: 1200, max: 3400, mean: 2200 },
        suggested: ["baseMetrics"]
      },
      {
        name: "Units",
        dataType: "INTEGER" as const,
        nullable: false,
        statistics: { distinctCount: 7, nullCount: 0, min: 500, max: 3000, mean: 1500 },
        suggested: ["volumeMetrics", "baseMetrics"]
      },
      {
        name: "Scenario",
        dataType: "STRING" as const,
        nullable: false,
        availableOptions: ["Actuals"],
        statistics: { distinctCount: 1, nullCount: 0 },
        suggested: ["scenario"]
      },
      {
        name: "Period",
        dataType: "STRING" as const,
        nullable: false,
        availableOptions: ["202411", "202412"],
        statistics: { distinctCount: 2, nullCount: 0 },
        suggested: ["period"]
      }
    ];
  }, []);

  const currentValue = controllerField.value || {};

  // Check if a column is already assigned
  const isColumnAssigned = useCallback((columnName: string): boolean => {
    const isAssigned = Object.values(currentValue).some((zoneColumns: any) => 
      Array.isArray(zoneColumns) && zoneColumns.some((col: any) => 
        typeof col === 'string' ? col === columnName : col.name === columnName
      )
    );
    
    return isAssigned;
  }, [currentValue]);

  // Validation logic
  const validateZones = useCallback(() => {
    const errors: Record<string, string> = {};
    
    // Use safe zones array to prevent forEach error
    zones.forEach(zone => {
      const assigned = currentValue[zone.id] || [];
      
      if (zone.required && assigned.length === 0) {
        errors[zone.id] = zone.validation?.message || `${zone.label} is required`;
      }
      
      if (!zone.multiple && assigned.length > 1) {
        errors[zone.id] = `${zone.label} accepts only one column`;
      }
      
      if (zone.maxItems && assigned.length > zone.maxItems) {
        errors[zone.id] = `${zone.label} accepts maximum ${zone.maxItems} columns`;
      }
    });
    
    return errors;
  }, [zones, currentValue]);

  const validationErrors = useMemo(() => validateZones(), [validateZones]);

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    
    // Determine what's being dragged
    if (active.id.toString().startsWith('available-')) {
      const columnName = active.id.toString().replace('available-', '');
      const column = availableColumns.find(col => col.name === columnName);
      if (column) {
        setActiveDragItem({
          id: active.id.toString(),
          type: 'column',
          data: column,
        });
      }
    } else {
      // Find the item in assigned columns
      for (const [zoneId, zoneColumns] of Object.entries(currentValue)) {
        const zoneItems = zoneColumns as (string | RateMetricConfig)[];
        for (let i = 0; i < zoneItems.length; i++) {
          const item = zoneItems[i];
          const itemId = typeof item === 'object' ? item.id : `${zoneId}-${item}`;
          if (itemId === active.id.toString()) {
            // Ensure we always have the full object for drag overlay
            let dragData: ColumnMetadata | RateMetricConfig;
            if (typeof item === 'object') {
              dragData = item;
            } else {
              // Look up the column metadata for string column names
              const columnMetadata = availableColumns.find(col => col.name === item);
              if (!columnMetadata) {
                logger.warn(`Column metadata not found for: ${item}`);
                return; // Skip if we can't find the metadata
              }
              dragData = columnMetadata;
            }

            setActiveDragItem({
              id: active.id.toString(),
              type: typeof item === 'object' ? 'custom-rate' : 'column',
              data: dragData,
              sourceZone: zoneId,
              index: i,
            });
            break;
          }
        }
      }
    }
  };

  // Handle drag over
  const handleDragOver = (event: DragOverEvent) => {
    const { over, active } = event;
    
    setOverId(over?.id || null);
    
    // Reset validation state
    setDragValidation({});
    
    // Add validation feedback for invalid drops
    if (over && active && activeDragItem?.type === 'column') {
      const overId = over.id.toString();
      const targetZone = zones.find(z => z.id === overId);
      
      if (targetZone) {
        const column = activeDragItem.data as ColumnMetadata;
        const newValidation: Record<string, { isValid: boolean; reason?: string }> = {};
        
        // Check all zones for validation status
        zones.forEach(zone => {
          const isValidDrop = !zone.accepts || zone.accepts.includes(column.dataType);
          
          if (!isValidDrop) {
            let reason = '';
            if (zone.accepts?.includes('INTEGER') || zone.accepts?.includes('DECIMAL')) {
              reason = 'Only numeric columns allowed';
            } else if (zone.accepts?.includes('STRING')) {
              reason = 'Only text columns allowed';
            } else {
              reason = `Only ${zone.accepts?.join(', ')} columns allowed`;
            }
            
            newValidation[zone.id] = {
              isValid: false,
              reason
            };
          } else {
            newValidation[zone.id] = { isValid: true };
          }
        });
        
        setDragValidation(newValidation);
      }
    }
  };

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveDragItem(null);
    setOverId(null);
    
    if (!over || !activeDragItem) {
      return;
    }

    const overId = over.id.toString();
    
    // Handle dropping on zones
    if (zones.some(zone => zone.id === overId)) {
      const targetZoneId = overId;
      const targetZone = zones.find(z => z.id === targetZoneId);
      
      if (!targetZone) {
        return;
      }
      
      // Check if zone accepts this item type
      if (activeDragItem.type === 'column') {
        const column = activeDragItem.data as ColumnMetadata;
        
        if (targetZone.accepts && !targetZone.accepts.includes(column.dataType)) {
          return; // Invalid drop
        }
      }
      
      const newValue = { ...currentValue };
      
      // Remove from source zone if moving within zones
      if (activeDragItem.sourceZone && activeDragItem.index !== undefined) {
        const sourceItems = [...(newValue[activeDragItem.sourceZone] || [])];
        sourceItems.splice(activeDragItem.index, 1);
        newValue[activeDragItem.sourceZone] = sourceItems;
      }
      
      // Add to target zone
      if (!newValue[targetZoneId]) {
        newValue[targetZoneId] = [];
      }
      
      const targetItems = [...newValue[targetZoneId]];
      
      // Check zone constraints
      if (!targetZone.multiple) {
        // Single-item zone: always accept, but replace if container already has an item
        const newItem = activeDragItem.type === 'column' 
          ? (activeDragItem.data as ColumnMetadata).name 
          : activeDragItem.data;
          
        if (targetItems.length > 0) {
          targetItems[0] = newItem; // Replace existing item
        } else {
          targetItems.push(newItem); // Add first item to empty container
        }
      } else if (!targetZone.maxItems || targetItems.length < targetZone.maxItems) {
        // Multiple-item zone: add new item if under limit
        targetItems.push(
          activeDragItem.type === 'column' 
            ? (activeDragItem.data as ColumnMetadata).name 
            : activeDragItem.data
        );
      }
      // If maxItems limit is reached and zone allows multiple, do nothing (silently reject drop)
      
      newValue[targetZoneId] = targetItems;
      
      controllerField.onChange(newValue);
      onFieldChange?.(field.name, newValue);
    }
    
    // Handle reordering within zones
    else if (activeDragItem.sourceZone && overId.includes(activeDragItem.sourceZone)) {
      const zoneId = activeDragItem.sourceZone;
      const zoneItems = [...(currentValue[zoneId] || [])];
      
      const activeIndex = activeDragItem.index!;
      const overIndex = zoneItems.findIndex((item, index) => {
        const itemId = typeof item === 'object' ? item.id : `${zoneId}-${item}`;
        return itemId === overId;
      });
      
      if (activeIndex !== overIndex && overIndex !== -1) {
        const reorderedItems = arrayMove(zoneItems, activeIndex, overIndex);
        const newValue = {
          ...currentValue,
          [zoneId]: reorderedItems,
        };
        
        controllerField.onChange(newValue);
        onFieldChange?.(field.name, newValue);
      }
    }
  };

  // Handle column removal from zone
  const handleRemove = useCallback((zoneId: string, index: number) => {
    const newValue = { ...currentValue };
    if (newValue[zoneId]) {
      const zoneItems = [...newValue[zoneId]];
      zoneItems.splice(index, 1);
      newValue[zoneId] = zoneItems;
      controllerField.onChange(newValue);
      onFieldChange?.(field.name, newValue);
    }
  }, [currentValue, controllerField, onFieldChange, field.name]);

  // Handle custom rate metric creation
  const handleCreateCustomRate = useCallback(() => {
    setCustomRateModalVisible(true);
  }, []);

  const handleCustomRateConfirm = useCallback((config: RateMetricConfig) => {
    const newValue = { ...currentValue };
    if (!newValue.rateMetrics) {
      newValue.rateMetrics = [];
    }
    newValue.rateMetrics = [...newValue.rateMetrics, config];
    
    controllerField.onChange(newValue);
    onFieldChange?.(field.name, newValue);
    setCustomRateModalVisible(false);
  }, [currentValue, controllerField, onFieldChange, field.name]);

  // Get assigned columns for each zone
  const getAssignedColumns = useCallback((zoneId: string): (string | RateMetricConfig)[] => {
    return currentValue[zoneId] || [];
  }, [currentValue]);

  // Available columns that haven't been assigned
  const availableUnassignedColumns = useMemo(() => {
    const result = availableColumns.filter(col => !isColumnAssigned(col.name));
    
    return result;
  }, [availableColumns, isColumnAssigned, currentValue]);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="column-mapper">
          <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
            {/* Available Columns Panel */}
            <div className="column-mapper__available">
              <h4 className="column-mapper__available-title">Available Columns</h4>
              <SortableContext 
                items={availableUnassignedColumns.map(col => `available-${col.name}`)} 
                strategy={verticalListSortingStrategy}
              >
                <div className="column-mapper__available-container">
                  <div className="column-mapper__available-list">
                    {availableColumns.length === 0 ? (
                      <div className="column-mapper__no-columns">
                        Upload a file to see available columns
                      </div>
                    ) : availableUnassignedColumns.length === 0 ? (
                      <div className="column-mapper__no-columns">
                        All columns have been assigned
                      </div>
                    ) : (
                      availableUnassignedColumns.map(column => (
                        <DraggableColumn
                          key={column.name}
                          column={column}
                          isAssigned={false}
                        />
                      ))
                    )}
                  </div>
                </div>
              </SortableContext>
            </div>

            {/* Drop Zones Panel */}
            <div className="column-mapper__zones">
              <SpaceBetween size="l">
                {zones.map(zone => (
                  <DroppableZone
                    key={zone.id}
                    zone={zone}
                    assignedColumns={getAssignedColumns(zone.id)}
                    availableColumns={availableColumns}
                    onRemove={(index) => handleRemove(zone.id, index)}
                    isOver={overId === zone.id}
                    dragValidation={dragValidation[zone.id]}
                    isDragging={!!activeDragItem}
                  />
                ))}
              </SpaceBetween>
            </div>
          </Grid>
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          <DragOverlayContent dragItem={activeDragItem} />
        </DragOverlay>
      </DndContext>

      {/* Custom Rate Metric Modal */}
      <CustomRateMetricModal
        visible={customRateModalVisible}
        onDismiss={() => setCustomRateModalVisible(false)}
        onConfirm={handleCustomRateConfirm}
        availableColumns={availableColumns}
      />
    </>
  );
};
